import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useApp } from "./AppContext";
import { useSalon } from "./SalonContext";
import { useFeatureAccess } from "./FeatureAccessContext";
import { LoyaltySettings, isConfiguredLoyaltySettings } from "../types/loyalty";

export interface LoyaltyProgramState {
  /** Feature-gate: whether the loyalty feature is available at all. */
  featureAvailable: boolean;
  /** The location the program is keyed on (businessInfo.id / salon uuid). */
  businessLocationId: string | null;
  /** NUMERIC business_locations.id from the settings GET — required by the
   *  client-loyalty endpoints (GET/adjust/opt-in). Null when the settings
   *  response is absent or omits it. */
  apiBusinessLocationId: number | null;
  /** GET settings response (configured row or backend defaults), null while
   *  loading / on failure. */
  settings: Partial<LoyaltySettings> | null;
  /** True when GET settings returned an existing configured row. */
  isConfigured: boolean;
  /** True when the location's program is configured AND enabled. */
  isEnabled: boolean;
  isLoading: boolean;
  /** Set when the GET failed (e.g. backend endpoints not deployed yet). */
  error: unknown | null;
  refresh: () => Promise<void>;
}

/** State surfaced when no provider is mounted (e.g. isolated unit tests):
 *  behaves exactly like "loyalty feature locked". */
const INERT_STATE: LoyaltyProgramState = {
  featureAvailable: false,
  businessLocationId: null,
  apiBusinessLocationId: null,
  settings: null,
  isConfigured: false,
  isEnabled: false,
  isLoading: false,
  error: null,
  refresh: async () => {},
};

const LoyaltyProgramContext = createContext<LoyaltyProgramState>(INERT_STATE);

/**
 * App-root loyalty program state. Fires ONE settings GET per location as soon
 * as businessInfo.id resolves — concurrent with the app's other boot fetches
 * instead of waterfalling per consuming surface. Refetches on location switch;
 * skipped entirely when the loyalty feature is locked.
 *
 * Backend failures (404/5xx — e.g. endpoints not deployed) degrade to "not
 * configured" so consumers stay on the empty-state / hidden paths.
 */
export function LoyaltyProgramProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { getLoyaltySettings } = useApp();
  const { businessInfo } = useSalon() as {
    businessInfo?: { id?: string; loyalty_enabled?: boolean } | null;
  };
  const { hasFeature } = useFeatureAccess();

  const featureAvailable = hasFeature("loyalty");
  const businessLocationId = businessInfo?.id ?? null;
  // Optimistic seed while (or if) the settings GET is outstanding: the backend
  // may expose loyalty_enabled on businessInfo (backend item 9); when present
  // it pre-lights isEnabled, and the GET remains authoritative once resolved.
  const seedEnabled = businessInfo?.loyalty_enabled === true;

  const [settings, setSettings] = useState<Partial<LoyaltySettings> | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(featureAvailable);
  const [error, setError] = useState<unknown | null>(null);

  const refresh = useCallback(async () => {
    if (!featureAvailable || !businessLocationId) {
      setSettings(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await getLoyaltySettings(businessLocationId);
      setSettings(response ?? null);
      setError(null);
    } catch (err) {
      // Endpoints may not exist yet — degrade to "not configured" without
      // surfacing a crash.
      console.error("Error loading loyalty settings:", err);
      setSettings(null);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [featureAvailable, businessLocationId, getLoyaltySettings]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo<LoyaltyProgramState>(() => {
    const isConfigured = isConfiguredLoyaltySettings(settings);
    const isEnabled =
      settings != null
        ? isConfigured && settings.enabled === true
        : featureAvailable && seedEnabled;

    // Numeric business_locations.id for the client-loyalty endpoints; the
    // backend 400s on the uuid. Defensive: may be absent on unconfigured rows.
    const rawApiId = settings?.business_location_id;
    const apiBusinessLocationId =
      typeof rawApiId === "number" && Number.isFinite(rawApiId)
        ? rawApiId
        : typeof rawApiId === "string" && /^\d+$/.test(rawApiId)
          ? Number(rawApiId)
          : null;

    return {
      featureAvailable,
      businessLocationId,
      apiBusinessLocationId,
      settings,
      isConfigured,
      isEnabled,
      isLoading,
      error,
      refresh,
    };
  }, [
    featureAvailable,
    businessLocationId,
    seedEnabled,
    settings,
    isLoading,
    error,
    refresh,
  ]);

  return (
    <LoyaltyProgramContext.Provider value={value}>
      {children}
    </LoyaltyProgramContext.Provider>
  );
}

export function useLoyaltyProgramContext(): LoyaltyProgramState {
  return useContext(LoyaltyProgramContext);
}
